import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import PrivacyPolicy from './PrivacyPolicy';
import DeleteAccount from './DeleteAccount';
import './App.css';
import './i18n';
import { useTranslation } from 'react-i18next';

const App = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
    </Routes>
  );
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState({});
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: "📱",
      title: "Multi-Platform Support",
      description: "Save content from TikTok, Instagram, X (Twitter), Facebook, Web, YouTube, and Pinterest all in one place"
    },
    {
      icon: "🏷️",
      title: "Smart Organization",
      description: "Create custom collections and tags to organize your saved content exactly how you want"
    },
    {
      icon: "🔍",
      title: "Powerful Search",
      description: "Find any saved link instantly with full-text search functionality"
    },
    {
      icon: "👁️",
      title: "Privacy Mode",
      description: "Switch to discreet mode or display only selected thumbnails for complete privacy"
    },
    {
      icon: "🌙",
      title: "Theme Options",
      description: "Choose between light and dark modes to match your preference"
    },
    {
      icon: "☁️",
      title: "Multi-Device Sync",
      description: "Access your collections seamlessly across all your phones and tablets"
    }
  ];

  const testimonials = [
    {
      name: "Sophie L.",
      role: "Content Creator",
      text: "Finally, an app that keeps all my inspiration organized! No more lost posts or forgotten videos.",
      rating: 5
    },
    {
      name: "Emma R.",
      role: "Digital Marketer",
      text: "Game-changer for my work. I can quickly find any saved content and organize campaigns efficiently.",
      rating: 5
    },
    {
      name: "Maya K.",
      role: "Social Media Manager",
      text: "The search feature is incredible. I can find any post I saved months ago in seconds!",
      rating: 5
    }
  ];

  const stats = [
    { number: "7", label: "Social Platforms" },
    { number: "∞", label: "Storage Capacity" },
    { number: "100%", label: "Private & Secure" },
    { number: "24/7", label: "Available Access" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleGooglePlayClick = () => {
    // This would redirect to Google Play Store
    window.open('https://play.google.com/store', '_blank');
  };

  // Custom Klipiz Logo Component
  const KlipizLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full rounded-2xl overflow-hidden shadow-lg" style={{ borderRadius: '25%' }}>
        <img
          src="/app_icon.png"
          alt="Klipiz Logo"
          className="w-full h-full object-cover"
          style={{ borderRadius: '25%' }}
        />
      </div>
    </div>
  );

  // Real Social Media Logo Components
  const TikTokLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-black rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const InstagramLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-400 rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M12 7.377a4.623 4.623 0 100 9.246 4.623 4.623 0 000-9.246zm0 7.627a3.004 3.004 0 110-6.008 3.004 3.004 0 010 6.008z"
            fill="white"
          />
          <circle cx="16.5" cy="7.5" r="1.125" fill="white"/>
          <path
            d="M7.5 3C5.25 3 3 5.25 3 7.5v9c0 2.25 2.25 4.5 4.5 4.5h9c2.25 0 4.5-2.25 4.5-4.5v-9C21 5.25 18.75 3 16.5 3h-9zm0 1.5h9c1.5 0 3 1.5 3 3v9c0 1.5-1.5 3-3 3h-9c-1.5 0-3-1.5-3-3v-9c0-1.5 1.5-3 3-3z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const FacebookLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const XLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-black rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const YouTubeLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-red-600 rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const PinterestLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-red-500 rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <path
            d="M0 12c0 5.123 3.211 9.497 7.73 11.218-.11-.937-.227-2.482.025-3.566.217-.932 1.401-5.938 1.401-5.938s-.357-.715-.357-1.774c0-1.66.962-2.9 2.161-2.9 1.02 0 1.512.765 1.512 1.682 0 1.025-.653 2.557-.99 3.978-.281 1.189.597 2.159 1.769 2.159 2.123 0 3.756-2.239 3.756-5.471 0-2.861-2.056-4.86-4.991-4.86-3.398 0-5.393 2.549-5.393 5.184 0 1.027.395 2.127.889 2.726a.36.36 0 0 1 .083.343c-.091.378-.293 1.189-.332 1.355-.053.218-.173.265-.4.159-1.499-.698-2.436-2.888-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.357-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.16 24c6.624 0 11.99-5.366 11.99-12C24 5.366 18.634.001 12.01.001 5.374.001.001 5.366.001 12.001z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );

  const WebLogo = ({ size = "32" }) => (
    <div className={`w-${size} h-${size} relative`} style={{ width: size + 'px', height: size + 'px' }}>
      <div className="w-full h-full bg-green-500 rounded-full flex items-center justify-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-3/4 h-3/4">
          <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="white" strokeWidth="2" fill="none"/>
          <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">WWW</text>
        </svg>
      </div>
    </div>
  );

  const socialNetworks = [
    { name: "TikTok", component: TikTokLogo },
    { name: "Instagram", component: InstagramLogo },
    { name: "X", component: XLogo },
    { name: "Facebook", component: FacebookLogo },
    { name: "Web", component: WebLogo },
    { name: "YouTube", component: YouTubeLogo },
    { name: "Pinterest", component: PinterestLogo }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-orange-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <KlipizLogo size="32" />
              <span className="text-xl font-bold text-gray-800">Klipiz</span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Sélecteur de langue intégré dans le header */}
              <div className="flex gap-2">
                <button
                  onClick={() => i18n.changeLanguage('fr')}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: '1px solid #f59e42',
                    background: i18n.language === 'fr' ? '#f59e42' : '#fff',
                    color: i18n.language === 'fr' ? '#fff' : '#f59e42',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >FR</button>
                <button
                  onClick={() => i18n.changeLanguage('en')}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: '1px solid #3b82f6',
                    background: i18n.language === 'en' ? '#3b82f6' : '#fff',
                    color: i18n.language === 'en' ? '#fff' : '#3b82f6',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >EN</button>
              </div>
              <button
                onClick={handleGooglePlayClick}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 font-semibold shadow-lg"
              >
                {t('getApp')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="pt-20 pb-16 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className={`lg:w-1/2 space-y-8 ${isVisible.hero ? 'animate-fade-in-up' : 'opacity-0'}`}>
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
                  {t('neverLose')} 
                  <span className="text-gradient bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"> {t('socialContent')}</span>
                  <br />{t('again')}
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {t('heroDescription')}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleGooglePlayClick}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 font-semibold shadow-xl text-lg"
                >
                  {t('downloadNow')}
                </button>
                <button className="border-2 border-orange-500 text-orange-600 px-8 py-4 rounded-full hover:bg-orange-50 transition-all duration-300 font-semibold text-lg">
                  {t('learnMore')}
                </button>
              </div>

              <div className="flex items-center space-x-8 pt-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{t(stat.label === 'Social Platforms' ? 'statPlatforms' : stat.label === 'Storage Capacity' ? 'statStorage' : stat.label === 'Private & Secure' ? 'statPrivate' : 'statAccess')}</div>
                  </div>
                ))}
              </div>

              {/* Real Social Network Logos with Animation */}
              <div className="flex items-center justify-center space-x-3 pt-6">
                <span className="text-sm text-gray-600 mr-4">{t('supports')}</span>
                {socialNetworks.map((network, index) => {
                  const LogoComponent = network.component;
                  return (
                    <div
                      key={network.name}
                      className="animate-bounce"
                      style={{ animationDelay: `${index * 0.2}s` }}
                      title={network.name}
                    >
                      <LogoComponent size="32" />
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="lg:w-1/2 mt-12 lg:mt-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
                <img
                  src="https://images.unsplash.com/photo-1603899122724-98440dd9c400"
                  alt="Klipiz App Hero"
                  className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl transform hover:scale-105 transition-all duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 ${isVisible.features ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {t('everythingYouNeed')} 
              <span className="text-gradient bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"> {t('organize')}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('featuresDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${
                  isVisible.features ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{t(`feature${feature.title.replace(/\s/g, '')}`)}</h3>
                <p className="text-gray-600">{t(`feature${feature.title.replace(/\s/g, '')}Desc`, { defaultValue: feature.description })}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="relative w-full max-w-lg mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
                <div className="bg-orange-500 px-4 py-2 text-white text-center font-semibold">
                  Klipiz
                </div>
                <div className="p-6 bg-gray-800">
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-white">
                          <path d="M13 2L3 14h5l-1 8 10-12h-5l1-8z" fill="currentColor"/>
                        </svg>
                      </div>
                      <span className="text-orange-500 font-semibold text-lg">Klipiz</span>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3 mb-4">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-sm">{t('searchContent')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="aspect-square bg-orange-500 rounded-lg mb-2 flex items-center justify-center">
                        <span className="text-white text-xs">🍳</span>
                      </div>
                      <div className="text-white text-xs">{t('chakchkouka')}</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="aspect-square bg-green-500 rounded-lg mb-2 flex items-center justify-center">
                        <span className="text-white text-xs">🥘</span>
                      </div>
                      <div className="text-white text-xs">{t('boulettesVeganes')}</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="aspect-square bg-red-500 rounded-lg mb-2 flex items-center justify-center">
                        <span className="text-white text-xs">🍓</span>
                      </div>
                      <div className="text-white text-xs">{t('framboise')}</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3">
                      <div className="aspect-square bg-pink-500 rounded-lg mb-2 flex items-center justify-center">
                        <span className="text-white text-xs">🍰</span>
                      </div>
                      <div className="text-white text-xs">{t('panacota')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 ${isVisible.testimonials ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {t('lovedBy')} 
              <span className="text-gradient bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"> {t('users')}</span>
            </h2>
            <p className="text-xl text-gray-600">{t('testimonialsDescription')}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
              
              <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                <div className="md:w-1/3">
                  <img
                    src="https://images.pexels.com/photos/13731868/pexels-photo-13731868.jpeg"
                    alt="Happy User"
                    className="w-32 h-32 rounded-full mx-auto object-cover shadow-lg"
                  />
                </div>
                
                <div className="md:w-2/3 text-center md:text-left">
                  <div className="flex justify-center md:justify-start mb-4">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <span key={i} className="text-orange-500 text-2xl">★</span>
                    ))}
                  </div>
                  <p className="text-lg text-gray-700 mb-6 italic">
                    "{testimonials[currentTestimonial].text}"
                  </p>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg">
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className="text-orange-600">{testimonials[currentTestimonial].role}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonial ? 'bg-orange-500' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-20 bg-gradient-to-r from-orange-500 to-orange-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className={`lg:w-1/2 space-y-6 ${isVisible.cta ? 'animate-fade-in-up' : 'opacity-0'}`}>
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                {t('readyToNeverLoseContent')}
              </h2>
              <p className="text-xl opacity-90">
                {t('ctaDescription')}
              </p>
              <div className="space-y-4">
                <button
                  onClick={handleGooglePlayClick}
                  className="bg-white text-orange-600 px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 font-semibold shadow-xl text-lg"
                >
                  {t('getSocialCollector')}
                </button>
                <div className="flex items-center space-x-4 text-sm opacity-75">
                  <span>{t('freeTrial')}</span>
                  <span>{t('cancelAnytime')}</span>
                  <span>{t('securePayment')}</span>
                </div>
              </div>

              {/* Real Social Network Logos with Animation in CTA */}
              <div className="flex items-center justify-start space-x-3 pt-6">
                <span className="text-sm opacity-75 mr-4">{t('worksWith')}</span>
                {socialNetworks.map((network, index) => {
                  const LogoComponent = network.component;
                  return (
                    <div
                      key={network.name + 'cta'}
                      className="animate-pulse"
                      style={{ animationDelay: `${index * 0.3}s` }}
                      title={network.name}
                    >
                      <LogoComponent size="40" />
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="lg:w-1/2 mt-12 lg:mt-0">
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 rounded-3xl blur-2xl opacity-20"></div>
                <div className="relative bg-gray-900 rounded-3xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-500">
                  <div className="bg-orange-500 px-4 py-3 text-white text-center font-semibold">
                    {t('socialCollectorConnection')}
                  </div>
                  <div className="p-8 bg-gray-800">
                    <div className="flex justify-center mb-8">
                      <div className="w-16 h-16 bg-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" className="w-10 h-10 text-white">
                          <path d="M13 2L3 14h5l-1 8 10-12h-5l1-8z" fill="currentColor"/>
                          <path d="M13 2L3 14h5l-1 8 5-6" fill="#3B82F6"/>
                        </svg>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="border-2 border-orange-500 rounded-lg p-3">
                        <div className="flex items-center space-x-2 text-orange-500">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                          </svg>
                          <span className="text-sm">{t('email')}</span>
                        </div>
                      </div>
                      <div className="border border-gray-600 rounded-lg p-3">
                        <div className="flex items-center space-x-2 text-gray-400">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                          </svg>
                          <span className="text-sm">{t('password')}</span>
                        </div>
                      </div>
                      <button className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                        {t('login')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <KlipizLogo size="32" />
                <span className="text-xl font-bold">Klipiz</span>
              </div>
              <p className="text-gray-400">
                {t('footerDescription')}
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('features')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>{t('featuresPlatforms')}</li>
                <li>{t('featuresOrganization')}</li>
                <li>{t('featuresSearch')}</li>
                <li>{t('featuresPrivacy')}</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('support')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>{t('helpCenter')}</li>
                <li>{t('contactSupport')}</li>
                <li><Link to="/privacy" className="hover:text-orange-500 transition-colors cursor-pointer">{t('privacyPolicy')}</Link></li>
                <li><Link to="/delete-account" className="hover:text-orange-500 transition-colors cursor-pointer">{i18n.language === 'fr' ? 'Supprimer mon compte' : 'Delete my account'}</Link></li>
                <li>{t('termsOfService')}</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('download')}</h4>
              <button
                onClick={handleGooglePlayClick}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 font-semibold"
              >
                {t('googlePlayStore')}
              </button>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Klipiz. {t('allRightsReserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};


export default App;